import useToast from "@/contexts/Toast/useToast";
import { exportTypeIndex, TypeOfExportProps } from "@/pages/ExportMeeting/constants";
import {
  AiIcon,
  AlarmIcon,
  BoldTextM,
  Breakpoint,
  Button,
  ButtonVariant,
  Color,
  HorizontalFlex,
  IconSize,
  MediumTextM,
  Modal,
  ModalSize,
  RecordVoiceIcon,
  ResponsiveGrid,
  Space,
  VerticalFlex,
} from "@enxcs/dap-ui-stratos";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomExportOptionsLegalNotice, ExportVerticalFlexButton } from "./RecordingExportOptionsModal.styles";
import usePostCreateMinutes from "@/hooks/mutations/usePostCreateMinutes";
import usePostCreateTranscript from "@/hooks/mutations/usePostCreateTranscript";
import usePostCreateSummary from "@/hooks/mutations/usePostCreateSummary";
import usePostCreateDetailedMinutes from "@/hooks/mutations/usePostCreateDetailedMinutes";
import { useQueryClient } from "@tanstack/react-query";

interface RecordingExportOptionsModalProps {
  recordingId: string;
  handleCloseModal: () => void;
  displayWaitingModal: () => void;
  handleRefresh?: () => void;
  exportTypes?: number[];
}

const RecordingExportOptionsModal: FC<RecordingExportOptionsModalProps> = ({
  recordingId,
  handleCloseModal,
  displayWaitingModal,
  handleRefresh,
  exportTypes,
}) => {
  const { t } = useTranslation();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const [showExportConfirmationModal, setShowExportConfirmationModal] = useState("");

  const { mutateAsync: mutateCreateMinutes, isPending: isLoadingMinutes } = usePostCreateMinutes();
  const { mutateAsync: mutateCreateTranscript, isPending: isLoadingTranscript } = usePostCreateTranscript();
  const { mutateAsync: mutateCreateSummary, isPending: isLoadingSummary } = usePostCreateSummary();
  const { mutateAsync: mutateCreateDetailedMinutes, isPending: isLoadingDetailedMinutes } = usePostCreateDetailedMinutes();

  const isLoading = isLoadingMinutes || isLoadingSummary || isLoadingTranscript || isLoadingDetailedMinutes;

  const checkExportAction = (exportType: TypeOfExportProps, exportTypeAvailable: boolean) => {
    if (exportTypeAvailable) {
      setShowExportConfirmationModal(exportType);
      return;
    } else {
      void exportFile(exportType);
    }
  };

  const exportFile = async (exportType: TypeOfExportProps) => {
    if (isLoading) return;

    try {
      switch (exportType) {
        case TypeOfExportProps.TRANSCRIPTION:
          await mutateCreateTranscript({ recordingId });
          break;
        case TypeOfExportProps.MINUTES:
          await mutateCreateMinutes({ recordingId });
          break;
        case TypeOfExportProps.SUMMARY:
          await mutateCreateSummary({ recordingId });
          break;
        case TypeOfExportProps.DETAILED_MINUTES:
          await mutateCreateDetailedMinutes({ recordingId });
          break;
      }
      const content = exportTypeIndex[exportType as keyof typeof exportTypeIndex];

      queryClient.removeQueries({ queryKey: ["transcriptionData", recordingId, content], exact: true });
      displayWaitingModal();
      handleCloseModal();

      if (handleRefresh) {
        handleRefresh();
      }
    } catch {
      showErrorToast(t("failMessage.toast"));
    }
  };

  return showExportConfirmationModal ? (
    <Modal
      size={ModalSize.M}
      title={t("exportModal.title")}
      closeTitle={t("exportModal.close")}
      body={<HorizontalFlex $pad={Space.V8}>{t(`exportModal.regenerate${showExportConfirmationModal}`)}</HorizontalFlex>}
      footer={
        <HorizontalFlex $justify="flex-end" $gap={Space.V8}>
          <Button variant={ButtonVariant.SECONDARY} onClick={handleCloseModal} label={t("buttons.cancel")} />
          <Button
            variant={ButtonVariant.PRIMARY}
            label={t("buttons.regenerate")}
            disabled={isLoading}
            onClick={() => {
              exportFile(showExportConfirmationModal as TypeOfExportProps);
            }}
          />
        </HorizontalFlex>
      }
      onClose={() => {
        handleCloseModal();
      }}
    />
  ) : (
    <Modal
      size={ModalSize.M}
      title={t("exportModal.title")}
      closeTitle={t("exportModal.close")}
      body={
        <VerticalFlex $gap={Space.V8} $justify="space-between" $align={"flex-start"}>
          <MediumTextM>{t("exportModal.chooseType")}</MediumTextM>

          <ResponsiveGrid $l={`repeat(2, 1fr)`} $gap={Space.V12}>
            <ExportVerticalFlexButton
              data-qa-id="export-transcription-button"
              id="export-transcription-button"
              onClick={() => checkExportAction(TypeOfExportProps.TRANSCRIPTION, exportTypes ? exportTypes.includes(0) : false)}
              $gap={Space.V8}
              $pad={Space.V8}
            >
              <HorizontalFlex $gap={Space.V8} $align="center" $justify="flex-start">
                <RecordVoiceIcon size={IconSize.V24} tone={Color.Teal.V30} />
                <BoldTextM>{t("exportModal.transcriptTitle")}</BoldTextM>
              </HorizontalFlex>

              <MediumTextM>{t("exportModal.transcriptDescription")}</MediumTextM>
            </ExportVerticalFlexButton>

            <ExportVerticalFlexButton
              data-qa-id="export-minutes-button"
              id="export-minutes-button"
              onClick={() => checkExportAction(TypeOfExportProps.MINUTES, exportTypes ? exportTypes.includes(1) : false)}
              $gap={Space.V8}
              $pad={Space.V8}
            >
              <HorizontalFlex $gap={Space.V8} $align="center" $justify="flex-start">
                <AlarmIcon size={IconSize.V24} tone={Color.Teal.V30} />
                <BoldTextM>{t("exportModal.minutesTitle")}</BoldTextM>
              </HorizontalFlex>

              <MediumTextM>{t("exportModal.minutesDescription")}</MediumTextM>
            </ExportVerticalFlexButton>

            <ExportVerticalFlexButton
              data-qa-id="export-summary-button"
              id="export-summary-button"
              onClick={() => checkExportAction(TypeOfExportProps.SUMMARY, exportTypes ? exportTypes.includes(2) : false)}
              $gap={Space.V8}
              $pad={Space.V8}
            >
              <HorizontalFlex $gap={Space.V8} $align="center" $justify="flex-start">
                <AlarmIcon size={IconSize.V24} tone={Color.Teal.V30} />
                <BoldTextM>{t("exportModal.summaryTitle")}</BoldTextM>
              </HorizontalFlex>

              <MediumTextM>{t("exportModal.summaryDescription")}</MediumTextM>
            </ExportVerticalFlexButton>

            <ExportVerticalFlexButton
              data-qa-id="export-detailed-minutes-button"
              id="export-detailed-minutes-button"
              onClick={() => checkExportAction(TypeOfExportProps.DETAILED_MINUTES, exportTypes ? exportTypes.includes(3) : false)}
              $gap={Space.V8}
              $pad={Space.V8}
            >
              <HorizontalFlex $gap={Space.V8} $align="center" $justify="flex-start">
                <AlarmIcon size={IconSize.V24} tone={Color.Teal.V30} />
                <BoldTextM>{t("exportModal.detailedMinutesTitle")}</BoldTextM>
              </HorizontalFlex>

              <MediumTextM>{t("exportModal.detailedMinutesDescription")}</MediumTextM>
            </ExportVerticalFlexButton>
          </ResponsiveGrid>

          <CustomExportOptionsLegalNotice $pad={Space.V16}>
            <AiIcon size={IconSize.V16} tone={Color.Blue.V50} />

            <MediumTextM style={{ color: Color.Neutral.V70 }}>{t("exportModal.legalNotice")}</MediumTextM>
          </CustomExportOptionsLegalNotice>
        </VerticalFlex>
      }
      footer={
        <HorizontalFlex $gap={Space.V8} $justify="flex-end" $breakpoint={Breakpoint.S}>
          <Button
            variant={ButtonVariant.GHOST}
            label={t("buttons.cancel")}
            onClick={() => {
              handleCloseModal();
            }}
            disabled={false}
          />
        </HorizontalFlex>
      }
      onClose={() => {
        handleCloseModal();
      }}
    />
  );
};

export default RecordingExportOptionsModal;
